<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <material-header title="Feedback"
                         info="Exit feedback from the panel members"
                         :loading="loading"
                         help="exit-feedback"
                         icon="mdi-comment-quote-outline">
            <template slot="buttons">

                <!-- LOCALE SELECTOR -->
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <div style="width: 150px" v-on="on">
                            <v-select
                                v-model="localeModel"
                                :items="availableLocales"
                                item-text="code"
                                item-value="code"
                                label="Select locale"
                                solo
                                hide-details
                                @change="localeChanged"
                            />
                        </div>
                    </template>
                    <span>Change locale</span>
                </v-tooltip>

                <!-- REFRESH -->
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission(minPermission)"
                               v-show="!!localeModel"
                               @click="refreshClicked()"
                               v-on="on"
                               :loading="loading"
                               fab
                               small
                               class="ml-8 mr-2 elevation-0"
                               color="primary">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>

            </template>

        </material-header>

        <v-alert v-if="!hasPermission(minPermission)"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <div v-if="hasPermission(minPermission)">

            <v-alert v-if="showMachineLearningNote"
                     class="body-2 mt-5"
                     type="info"
            >
                Selected language is not supported for AI based key-phrase or sentiment analysis. However we analyzed frequency
                of words for you.
            </v-alert>

            <v-alert v-if="data.feedback.length >0 && data.feedback.length <= 5"
                     class="body-2 mt-5"
                     type="info"
            >
                Word cloud will automatically display when you have received at least 5 feedbacks.
            </v-alert>

            <v-alert v-if="data.feedback.length === 0"
                     class="body-2 mt-5"
                     type="info"
                     prominent
            >
                No feedback found.
            </v-alert>

            <!-- CHART -->
            <div class="chart" ref="chartdiv" v-if="data.feedback.length > 5"></div>

            <!-- FEEDBACK CARDS -->
            <div class="d-inline-flex flex-wrap justify-left mr-0 ml-0">

                <div v-for="item in data.feedback" :key="item.id">

                    <v-scale-transition>

                        <v-card
                            :min-width="300"
                            :max-width="300"
                            :class="commentBoxClass(item)">

                            <div class="comment-area">
                                <v-icon color="grey">mdi-format-quote-open</v-icon>

                                <v-tooltip top min-width="500" max-width="500">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on" class="grey--text text--lighten-2" style="cursor: help">
                                            {{ shorten(item.comment) }}
                                        </div>
                                    </template>
                                    <v-container>
                                        <v-row dense style="flex-wrap: nowrap;">
                                            <v-col cols=6 class="flex-shrink-0">
                                                Sentiment:
                                            </v-col>
                                            <v-col cols=6 flex-grow-1 class="flex-grow-1">
                                                <v-chip label small :color="sentimentColor(item.sentiment)">
                                                    {{ item.sentiment }}
                                                </v-chip>
                                            </v-col>
                                        </v-row>
                                        <v-row dense style="flex-wrap: nowrap;" v-if="item.project_id">
                                            <v-col cols=6 class="flex-shrink-0">
                                                Project:
                                            </v-col>
                                            <v-col cols=6 class="flex-grow-1">
                                                {{ item.project_id }} {{ item.project_name }}
                                            </v-col>
                                        </v-row>
                                        <v-row dense style="flex-wrap: nowrap;">
                                            <v-col cols=6 class="flex-shrink-0">
                                                Created:
                                            </v-col>
                                            <v-col cols=6 class="flex-grow-1">
                                                {{ toHumanFrom(item.created_at) }}
                                            </v-col>
                                        </v-row>
                                        <v-row dense>
                                            <v-col cols=12>
                                                <v-card class="mt-2">
                                                    <v-card-text>
                                                        {{ item.comment }}
                                                    </v-card-text>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </v-container>

                                </v-tooltip>

                                <div class="text-right">
                                    <v-icon color="grey">mdi-format-quote-close</v-icon>
                                </div>
                            </div>

                            <div class="d-flex justify-space-between comment-bottom">

                                <div class="caption project grey--text">
                                    <v-chip v-if="item.project_id"
                                            small
                                            label
                                            color="grey darken-1"
                                            style="opacity: 0.7"
                                            class="grey--text text--lighten-3">
                                        {{ item.project_id }} {{ item.project_name }}
                                    </v-chip>
                                </div>

                                <div class="text-right caption grey--text">
                                    {{ toHumanFrom(item.created_at) }}
                                </div>
                            </div>

                        </v-card>
                    </v-scale-transition>
                </div>

                <footer-note class="mt-3" v-show="data.feedback.length > 0">
                    Report was generated {{ toHumanFrom(data.feedback.createdAt) }}.
                    {{ data.feedback.length > 100 ? 'The last 100' : 'All submitted' }} comments were processed.
                </footer-note>

            </div>
        </div>

    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../store';
import {mapState, mapActions} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4plugins_wordCloud from "@amcharts/amcharts4/plugins/wordCloud";
import FooterNote from "../../../components/UI/FooterNote";

const vuexModule = 'Feedback';

export default {

    mixins: [activeSessionChecker, timeDisplay],

    components: {
        Json,
        MaterialHeader,
        FooterNote
    },

    data() {
        return {
            minPermission: 'view panel settings',
            locale: null,
            localeMenuModel: false,
            showMachineLearningNote: false,
            loading: false,
        }
    },
    computed: {

        ...mapState(vuexModule, [
            'data',
            'feedbackLoading',
        ]),

        localeModel: {
            get() {
                return this.locale ? this.locale : null
            },
            set(value) {
                this.locale = value;
            }
        },

        availableLocales() {
            return this.$store.state.PanelSettings.settings.locales;
        }
    },

    watch: {
        availableLocales(locales) {
            this.locale = locales[0];
        }
    },

    methods: {

        ...mapActions(vuexModule, [
            'loadFeedback'
        ]),

        commentBoxClass(item) {
            return ['mt-2', 'mr-2', 'pa-3', 'comment-box-' + item.sentiment.toLowerCase()].join(' ');
        },

        refreshClicked() {
            this.getFeedback(true);
        },

        localeChanged() {
            this.getFeedback(true);
        },

        getFeedback(force) {

            if (this.locale) {

                this.loading = true;

                this.loadFeedback({locale: this.locale, force: force})
                    .then((data) => {

                        this.createChart();
                    })
                    .catch((error) => {
                        console.log(error);
                        Bus.$emit('flash-message', {type: 'error', message: 'OOPS! Feedback failed to load!'});
                        throw new Error(error);
                    })
                    .finally(() => {
                        this.loading = false;
                    })
            }
        },

        createChart() {
            let chart = am4core.create(this.$refs.chartdiv, am4plugins_wordCloud.WordCloud);

            let series = chart.series.push(new am4plugins_wordCloud.WordCloudSeries());

            series.maxCount = 100;
            series.minWordLength = 2;
            series.accuracy = 4;
            series.step = 15;
            series.rotationThreshold = 0.7;
            series.labels.template.tooltipText = "{word}: {value}";
            series.fontFamily = "Courier New";
            series.maxFontSize = am4core.percent(30);

            series.heatRules.push({
                "target": series.labels.template,
                "property": "fill",
                "min": am4core.color("#329EF4"),
                "max": am4core.color("#ff5555"),
                "dataField": "value"
            });

            series.dataFields.word = 'phrase';
            series.dataFields.value = 'count';

            series.data = this.data.phrases;
        },

        shorten(text) {
            if (text.length > 95) return text.substr(0, 95) + "...";
            return text;
        },

        sentimentColor(sentiment) {
            if (sentiment === "POSITIVE") return '#55AA55';
            if (sentiment === "NEGATIVE") return '#CC5555';
            if (sentiment === "NEUTRAL") return '#CCCCCC';
            if (sentiment === "MIXED") return '#CD8313';
        },
    },

    mounted() {
        this.getFeedback();
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    },
}

</script>

<style scoped>

.chart {
    min-height: 500px;
}

.project {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.comment-box-positive {
    border-left: 4px solid #55AA55;
}

.comment-box-negative {
    border-left: 4px solid #AA5555;
}

.comment-box-neutral {
    border-left: 4px solid #CCCCCC;
}

.comment-box-mixed {
    border-left: 4px solid #CD8313;
}

.comment-area {
    height: 130px;
}

.comment-bottom {
    height: 25px;
}

</style>